import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Phone, TrendingUp, AlertCircle, CheckCircle, DollarSign, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const StaffCollectionsProfilePage = () => {
  const { businessId, staffId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [staffId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/part4/collections/staff/${staffId}/profile`);
      setProfile(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to load staff profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CLEAR': return 'bg-green-100 text-green-700 border-green-300';
      case 'PARTIAL_DUE': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'UNPAID': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const filteredParties = profile?.parties?.filter((party) => {
    const matchesFilter = !filter || party.collection_status === filter;
    const matchesSearch = !searchText || 
      party.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (party.mobile && party.mobile.includes(searchText));
    return matchesFilter && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm">Loading staff profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Staff profile not found</p>
      </div>
    );
  }

  const summary = profile.today_summary;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button onClick={() => navigate(-1)} variant="outline" size="sm" className="w-fit">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Staff Collections Profile</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Detailed collections overview</p>
        </div>
      </div>

      {/* Staff Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {profile.staff_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{profile.staff_name}</h2>
              {profile.mobile && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <Phone className="w-4 h-4" />
                  {profile.mobile}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <User className="w-4 h-4" />
              <span className="text-xs">Assigned</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{summary.assigned_parties}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Unpaid</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{summary.unpaid}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 text-orange-600 mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Partial</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{summary.partial}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">Clear</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{summary.clear}</div>
          </CardContent>
        </Card>

        <Card className="col-span-2 sm:col-span-3 lg:col-span-1">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Collected Today</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-blue-600">₹{summary.collected?.toFixed(0)}</div>
            <div className="text-xs text-gray-500 mt-1">of ₹{summary.expected_collection?.toFixed(0)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Card */}
      {profile.performance && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600">Success Rate</div>
                <div className="text-2xl font-bold text-gray-900">
                  {(profile.performance.collection_success_rate * 100).toFixed(0)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Avg Collection Time</div>
                <div className="text-2xl font-bold text-gray-900">
                  {profile.performance.avg_collection_time_minutes} min
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Escalations</div>
                <div className="text-2xl font-bold text-gray-900">
                  {profile.performance.escalations}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name or mobile..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
            >
              <option value="">All Status</option>
              <option value="UNPAID">Unpaid Only</option>
              <option value="PARTIAL_DUE">Partial Due</option>
              <option value="CLEAR">Clear Only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Parties List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Assigned Parties ({filteredParties.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredParties.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm sm:text-base">No parties match your filters</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Party Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Outstanding</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredParties.map((party) => (
                      <tr key={party.party_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm">{party.name}</div>
                          {party.address && (
                            <div className="text-xs text-gray-500">{party.address}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{party.mobile || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(party.collection_status)}`}>
                            {party.collection_status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">
                          ₹{party.total_outstanding_amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {party.last_visit_date 
                            ? new Date(party.last_visit_date).toLocaleDateString() 
                            : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredParties.map((party) => (
                  <div key={party.party_id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{party.name}</div>
                        {party.address && (
                          <div className="text-xs text-gray-500 mt-1">{party.address}</div>
                        )}
                        {party.mobile && (
                          <div className="text-xs text-gray-600 mt-1">{party.mobile}</div>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(party.collection_status)}`}>
                        {party.collection_status?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t">
                      <div className="text-xs text-gray-500">Outstanding</div>
                      <div className="font-semibold text-sm">₹{party.total_outstanding_amount?.toFixed(2)}</div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <div className="text-gray-500">Last Visit</div>
                      <div>
                        {party.last_visit_date 
                          ? new Date(party.last_visit_date).toLocaleDateString() 
                          : 'Never'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffCollectionsProfilePage;
